import type { paths } from '@connected/api-client/api.generated';
import type { FetchOptions } from 'openapi-fetch';

import { useObjectUrl } from '@connected/api-client/hooks/use-object-url';
import { useMemo } from 'react';

import { useElectricalProjectFileDownload } from '@/hooks/use-electrical-project-file-download';

export type UseFilePreviewOptions = {
    query: FetchOptions<paths['/api/v2/electrical/projects/files/download']['get']>['params']['query'];
    enabled?: boolean;
};

export type UseFilePreviewResult = (
    | {
        isLoading: false;
        isError: false;
        url: string;
        metadata: {
            contentType: string;
            name: string;
            size: number;
        };
    }
    | {
        isLoading: true;
        isError: false;
        url: null;
        metadata: null;
    }
    | {
        isLoading: false;
        isError: true;
        url: null;
        metadata: null;
    }
);

export function useElectricalProjectFilePreview({ query, enabled = true }: UseFilePreviewOptions): UseFilePreviewResult {
    // Use the download hook to get the file
    const { isLoading, isError, file } = useElectricalProjectFileDownload({ query, enabled });

    // Use the object URL hook to get a deduplicated URL
    const { url } = useObjectUrl({
        file,
        cacheKey: query.ElectricalProjectFileId ? `electrical-project-file-${query.ElectricalProjectFileId}` : undefined,
        enabled,
    });

    // Derive metadata from the file
    const metadata = useMemo(() => {
        if (!file)
            return null;
        return {
            contentType: file.type,
            name: file.name,
            size: file.size,
        };
    }, [file]);

    // Return a clean, consistent result
    return {
        isLoading,
        isError,
        url,
        metadata,
    } as UseFilePreviewResult;
}
